/**
 * Session Correlation
 *
 * Manages session-scoped and flow-scoped correlation IDs stored in sessionStorage.
 * Enables tracing frontend actions through backend services.
 */

const SESSION_ID_KEY = 'obs_session_id';
const FLOW_ID_KEY = 'obs_flow_id';

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }
  // Fallback for older browsers
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Gets or creates a persistent session ID (survives page refreshes within tab).
 */
export function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') {
    return generateId();
  }

  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Gets or creates a flow-scoped correlation ID.
 * Call startNewFlow() to reset when a new user flow begins (e.g., checkout start).
 */
export function getFlowCorrelationId(): string {
  if (typeof sessionStorage === 'undefined') {
    return generateId();
  }

  let flowId = sessionStorage.getItem(FLOW_ID_KEY);
  if (!flowId) {
    flowId = generateId();
    sessionStorage.setItem(FLOW_ID_KEY, flowId);
  }
  return flowId;
}

/**
 * Starts a new flow and returns the new correlation ID.
 */
export function startNewFlow(): string {
  const flowId = generateId();
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(FLOW_ID_KEY, flowId);
  }
  return flowId;
}

/**
 * Generates a one-off correlation ID (not stored).
 */
export function generateCorrelationId(): string {
  return generateId();
}
